import React, { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle2, XCircle, Ban, RotateCcw, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import StatusBadge from "../StatusBadge";

const RISK_STYLE = { NORMAL: "text-emerald-400", REVIEW: "text-indigo-300", BLOCKED: "text-rose-400" };
const KYC_LABELS = { pan: "PAN Card", gst: "GST Certificate", business: "Business Proof" };
const TABS = ["Overview", "Subscriptions", "Payments", "KYC", "Fraud", "Audit"];

export default function MerchantDetail({ merchantId, onBack }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/merchants/${merchantId}`).then((r) => setD(r.data)).catch(() => toast.error("Could not load merchant"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [merchantId]);

  const act = async (action, needReason) => {
    let reason = "";
    if (needReason) { reason = prompt(`Reason for ${action}?`) || ""; if (action === "REJECT" && !reason) return; }
    setBusy(true);
    try { await api.post("/admin/merchants/status", { merchant_id: merchantId, action, reason }); toast.success(`${action} done`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const viewDoc = async (doc_id) => {
    try {
      const res = await api.get(`/admin/kyc/file/${doc_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open document"); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;
  const p = d.profile;
  const kycStatus = d.kyc_status === "PENDING" ? "PENDING_KYC" : d.kyc_status;

  return (
    <div className="space-y-5" data-testid="admin-merchant-detail">
      <button data-testid="merchant-detail-back" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to merchants</button>

      <div className="ledger-card rounded-2xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold">{p.business_name}</h1>
            <p className="font-mono text-xs text-slate-500">{p.owner_email || "—"} · {p.owner_name || ""}</p>
            <p className="mt-1 font-mono text-[11px] text-slate-600">{p.merchant_id} · GSTIN {p.gst || "—"} · PAN {p.pan || "—"}</p>
            <div className="mt-2 flex items-center gap-2">
              <StatusBadge status={kycStatus} />
              <StatusBadge status={p.subscription_status === "ACTIVE" ? "ACTIVE" : "REQUESTED"} />
              <span className={`font-mono text-xs ${RISK_STYLE[d.risk_state]}`}>Risk: {d.risk_state}</span>
            </div>
            {d.kyc_note && <p className="mt-1 text-xs text-rose-300">Note: {d.kyc_note}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Btn testid="merchant-approve" icon={CheckCircle2} label="Approve" onClick={() => act("APPROVE")} disabled={busy} className="border-emerald-500/40 text-emerald-300" />
            <Btn testid="merchant-reject" icon={XCircle} label="Reject" onClick={() => act("REJECT", true)} disabled={busy} className="border-rose-500/40 text-rose-300" />
            <Btn testid="merchant-suspend" icon={Ban} label="Suspend" onClick={() => act("SUSPEND", true)} disabled={busy} className="border-amber-500/40 text-amber-300" />
            <Btn testid="merchant-reactivate" icon={RotateCcw} label="Reactivate" onClick={() => act("REACTIVATE")} disabled={busy} className="border-slate-600 text-slate-300" />
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Net contribution" value={d.financials.net.display} accent />
          <Stat label="GST paid" value={d.financials.gst.display} />
          <Stat label="Cashback generated" value={d.financials.cashback_generated.display} />
          <Stat label="Tokens (avail/total)" value={`${d.tokens.available}/${d.tokens.plan_total}`} />
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-800 pb-2">
        {TABS.map((t) => (
          <button key={t} data-testid={`merchant-tab-${t.toLowerCase()}`} onClick={() => setTab(t)} className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium ${tab === t ? "bg-emerald-500 text-slate-950" : "text-slate-400 hover:text-white"}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Plan" value={d.plan?.name || p.current_plan_id || "—"} />
          <Stat label="Gross revenue" value={d.financials.gross.display} />
          <Stat label="Tokens issued" value={d.tokens.allocated_issued} />
          <Stat label="Tokens used" value={d.tokens.used} />
          <Stat label="Cashback count" value={d.financials.cashback_count} />
          <Stat label="₹0 cashback" value={d.financials.cashback_zero_count} />
          <Stat label="Subscriptions" value={d.subscriptions.length} />
          <Stat label="Bank" value={p.bank_account || "—"} />
        </div>
      )}
      {tab === "Subscriptions" && (
        <Mini cols={["Date", "Plan", "Gross", "GST", "Net", "Status"]} testid="merchant-subscriptions">
          {d.subscriptions.map((s) => (
            <tr key={s.sub_id} className="border-t border-slate-800/60">
              <td className="p-3 text-slate-400">{new Date(s.created_at).toLocaleDateString("en-IN")}</td>
              <td className="p-3 font-mono text-xs">{s.plan_id}</td>
              <td className="p-3 font-mono">₹{(s.gross_paise / 100).toLocaleString("en-IN")}</td>
              <td className="p-3 font-mono text-slate-400">₹{(s.gst_paise / 100).toLocaleString("en-IN")}</td>
              <td className="p-3 font-mono text-emerald-400">₹{(s.net_paise / 100).toLocaleString("en-IN")}</td>
              <td className="p-3"><StatusBadge status={s.status === "ACTIVE" ? "ACTIVE" : "PENDING"} /></td>
            </tr>
          ))}
          {d.subscriptions.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No subscriptions.</td></tr>}
        </Mini>
      )}
      {tab === "Payments" && (
        <Mini cols={["Date", "Order", "Amount", "Status"]} testid="merchant-payments">
          {d.payments.map((pay) => (
            <tr key={pay.payment_id} className="border-t border-slate-800/60">
              <td className="p-3 text-slate-400">{new Date(pay.created_at).toLocaleDateString("en-IN")}</td>
              <td className="p-3 font-mono text-xs text-slate-500">{pay.gatewayOrderId}</td>
              <td className="p-3 font-mono">₹{(pay.amount_paise / 100).toLocaleString("en-IN")}</td>
              <td className="p-3 font-mono text-xs">{pay.status}</td>
            </tr>
          ))}
          {d.payments.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500">No payments.</td></tr>}
        </Mini>
      )}
      {tab === "KYC" && (
        <div className="ledger-card rounded-2xl p-5" data-testid="merchant-kyc">
          <div className="flex flex-wrap gap-2">
            {d.kyc_documents.map((dd) => (
              <button key={dd.doc_id} data-testid={`merchant-kyc-view-${dd.doc_id}`} onClick={() => viewDoc(dd.doc_id)} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-xs hover:border-emerald-500/50">
                <FileText className="h-3.5 w-3.5 text-emerald-400" /> {KYC_LABELS[dd.doc_type] || dd.doc_type}
              </button>
            ))}
            {d.kyc_documents.length === 0 && <span className="text-sm text-slate-500">No KYC documents uploaded.</span>}
          </div>
        </div>
      )}
      {tab === "Fraud" && (
        <Mini cols={["Date", "State", "Reason"]} testid="merchant-fraud">
          {d.fraud_flags.map((f) => (
            <tr key={f.flag_id} className="border-t border-slate-800/60">
              <td className="p-3 text-slate-400">{new Date(f.created_at).toLocaleString("en-IN")}</td>
              <td className={`p-3 font-mono text-xs ${RISK_STYLE[f.risk_state]}`}>{f.risk_state}</td>
              <td className="p-3 text-slate-300">{f.reason}</td>
            </tr>
          ))}
          {d.fraud_flags.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-slate-500">No fraud history.</td></tr>}
        </Mini>
      )}
      {tab === "Audit" && (
        <Mini cols={["Date", "Action", "Actor"]} testid="merchant-audit">
          {d.audit.map((a) => (
            <tr key={a.audit_id} className="border-t border-slate-800/60">
              <td className="p-3 text-slate-400">{new Date(a.created_at).toLocaleString("en-IN")}</td>
              <td className="p-3 font-mono text-xs text-emerald-400">{a.action}</td>
              <td className="p-3 font-mono text-xs text-slate-500">{a.actor_id}</td>
            </tr>
          ))}
          {d.audit.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-slate-500">No audit events.</td></tr>}
        </Mini>
      )}
    </div>
  );
}

function Btn({ icon: Icon, label, onClick, disabled, className, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-white/5 disabled:opacity-50 ${className}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}
function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-1 font-heading text-lg font-bold ${accent ? "text-emerald-400" : "text-slate-100"}`}>{value}</p>
    </div>
  );
}
function Mini({ cols, children, testid }) {
  return (
    <div className="ledger-card overflow-hidden rounded-2xl">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="bg-slate-900/50 font-mono text-[11px] uppercase tracking-wider text-slate-500"><tr>{cols.map((c, i) => <th key={i} className="p-3 text-left">{c}</th>)}</tr></thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}
